import { UsersFormData } from '@/types/usersFormData'
import { css, Theme } from '@emotion/react'
import { RoleSelect } from '@/components/select/RoleSelect'
import { BaseInput } from '@/components/input/BaseInput'
import { FormFieldRow } from '@/components/form/FormFieldRow'
import { FormButtonArea } from '@/components/form/FormButtonArea'
import { useAtom } from 'jotai'
import { isDisabledAtom } from '@/store/state'

const formStyle = css`
  display: flex;
  flex-direction: column;
`

const inputStyle = css`
  width: 344px;
  height: 26px;
  font-size: 14px;
`

const selectStyle = css`
  width: 202px;
  height: 26px;
  font-size: 14px;
`

// 白い枠のコンテナ
const formContainerStyle = css`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 32px;
  width: 918px;
  max-width: 100%;
  margin-top: 24px;
`

// フォームヘッダー（タイトル + 必須注記）
const formHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

// セクションタイトル
const sectionTitleStyle = (t: Theme) => css`
  font-size: 1.8rem;
  font-weight: 400;
  line-height: 1.8rem;
  color: ${t.c.ec.black};
`

// 必須項目注記
const requiredNoteStyle = (t: Theme) => css`
  font-size: 1.4rem;
  color: ${t.c.ec.black};
`

// 必須項目注記内の「*」マーク
const requiredMarkInNote = css`
  color: #dc3545;
`

type Props = {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  formData: UsersFormData
  errors: { [key: string]: string }
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelectChange: (newValue: { label: string; value: number } | null) => void
  onCancel: () => void
  isCreate: boolean
  id?: string
}

export const UserForm = ({
  formData,
  errors,
  onChange,
  onSelectChange,
  onSubmit,
  onCancel,
  isCreate,
  id,
}: Props) => {
  const [isDisabled] = useAtom(isDisabledAtom)

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const formEvent = new Event('submit', { bubbles: true, cancelable: true })
    onSubmit(formEvent as any)
  }

  return (
    <>
      {/* 白い枠のコンテナ */}
      <div css={formContainerStyle}>
        {/* フォームヘッダー */}
        <div css={formHeaderStyle}>
          <h2 css={sectionTitleStyle}>アカウント情報</h2>
          <span css={requiredNoteStyle}>
            <span css={requiredMarkInNote}>*</span>は必須項目です。
          </span>
        </div>

        {/* フォーム */}
        <form css={formStyle}>
          <FormFieldRow
            label="ID"
            required
            error={errors.loginId}
            helpText="半角英数字と記号[@._+-]30文字以内で入力してください"
          >
            <BaseInput
              type="text"
              name="loginId"
              value={formData.loginId}
              maxLength={30}
              onChange={onChange}
              addStyle={inputStyle}
              isError={!!errors.loginId}
            />
          </FormFieldRow>

          <FormFieldRow label="アカウント名" required error={errors.name}>
            <BaseInput
              type="text"
              name="name"
              value={formData.name}
              maxLength={50}
              onChange={onChange}
              addStyle={inputStyle}
              isError={!!errors.name}
            />
          </FormFieldRow>

          <FormFieldRow
            label="パスワード"
            required={isCreate}
            error={errors.password}
            helpText="半角英数字8文字以上20文字以内で入力してください"
          >
            <BaseInput
              type="password"
              name="password"
              value={formData.password}
              maxLength={20}
              onChange={onChange}
              addStyle={inputStyle}
              isError={!!errors.password}
              autoComplete="new-password"
            />
          </FormFieldRow>

          <FormFieldRow label="権限" required error={errors.role}>
            <RoleSelect
              onChange={onSelectChange}
              initialValue={formData.role}
              addStyle={selectStyle}
              isError={!!errors.role}
            />
          </FormFieldRow>
        </form>
      </div>

      {/* ボタンエリア（白い枠の外） */}
      <FormButtonArea
        cancelUrl="/users"
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={id ? '更新' : '登録'}
        isDisabled={isDisabled}
        showError={Object.values(errors).some(
          (error) => error && error.trim() !== '',
        )}
      />
    </>
  )
}
